import CheckmarkIcon from 'assets/images/svg/checkmark.svg'; // Adjust the path as necessary
import NoIcon from 'assets/images/svg/no.svg'; // Adjust the path as necessary
import React, { useState } from 'react';

import './MutationMessage.scss';

import { Content, Property } from '../MessagesContext';

interface MutationMessageProps {
  content: Content | undefined; // The entire JSON object passed into MutationMessage
  sender: boolean;
}

const MutationMessage = ({ content, sender }: MutationMessageProps): React.ReactElement => {
  const [confirmed, setConfirmed] = useState<boolean | null>(null); // Track if the user has confirmed the action

  // Define a mapping of message types to display functions
  const messageHandlers: { [key: string]: () => React.ReactNode } = {
    // eslint-disable-next-line @typescript-eslint/naming-convention
    NEW_CAMPAIGN: () => {
      const campaignName = content?.input?.content['application/json'].properties?.find(
        (property: Property) => property.name === 'name'
      )?.value;
      const budget = content?.input?.content['application/json'].properties?.find(
        (property: Property) => property.name === 'budget'
      )?.value;

      if (confirmed === null) {
        return (
          <>
            <i>The agent attempted to create a campaign with the name</i>{' '}
            <strong>&quot;{campaignName as string}&quot;</strong> <i>and budget</i>{' '}
            <strong>&quot;{budget as string}&quot;</strong>.<i> Would you like to allow this?</i>
          </>
        );
      } else if (confirmed) {
        return (
          <>
            <i>Campaign created successfully.</i> <span className='clickable'>View here.</span>
          </>
        );
      } else {
        return <i>Campaign creation was denied.</i>;
      }
    },
    // eslint-disable-next-line @typescript-eslint/naming-convention
    NEW_ACCOUNT_LIST: () => {
      const accountName = content?.input?.content['application/json'].properties?.find(
        (property: Property) => property.name === 'name'
      )?.value;
      return (
        <>
          <i>The agent created a new account list named</i>{' '}
          <span className='clickable'>
            <strong>&quot;{accountName as string}&quot;</strong>
          </span>
          .
        </>
      );
    },
    // eslint-disable-next-line @typescript-eslint/naming-convention
    FILTER_APPLIED: () => <i>The agent updated the view.</i>,
    // eslint-disable-next-line @typescript-eslint/naming-convention
    NEW_EXPORT: () => (
      <>
        <i>Export generated.</i> <span className='clickable'>Click here to download.</span>
      </>
    ),
  };

  // Get the message text using the handler or a default message
  const displayText = (content && messageHandlers[content.type]?.()) || (
    <i>The assistant performed an action.</i>
  );

  // Render the confirmation buttons for the NEW_CAMPAIGN type with the choice-button class
  const renderConfirmationButtons = () => (
    <div className='message-choices'>
      <button onClick={() => setConfirmed(true)} className='choice-button'>
        <img src={CheckmarkIcon} alt='Checkmark' className='icon' /> <span>Yes, continue</span>
      </button>
      <button onClick={() => setConfirmed(false)} className='choice-button'>
        <img src={NoIcon} alt='No' className='icon' /> <span>No</span>
      </button>
    </div>
  );

  return (
    <div className={`message-bubble ${sender ? 'user-message' : 'system-message'}`}>
      {displayText}
      {content?.type === 'NEW_CAMPAIGN' && confirmed === null && renderConfirmationButtons()}
    </div>
  );
};

export default MutationMessage;
